import React, { useEffect, useState } from 'react';
import { Plus, Package, Search, Trash2, Edit2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useModal } from '../components/AppModal';
import DecimalInput from '../components/DecimalInput';
import OwnerSelect from '../components/OwnerSelect';
import useUserPreference from '../hooks/useUserPreference';
import useViewMode from '../hooks/useViewMode';
import useFieldLayout from '../hooks/useFieldLayout';
import VistaControles from '../components/VistaControles';
import DataTable from '../components/DataTable';

const initialForm = {
  id: null,
  code: '',
  name: '',
  description: '',
  unitPrice: '',
  taxRate: '21',
  unit: 'unidad',
  isActive: true,
  ownerId: null
};

const Products = () => {
  const { hasPermission } = useAuth();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useUserPreference('products:search', '');
  const [viewMode, setViewMode] = useViewMode('products');
  const fl = useFieldLayout('products', viewMode);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [productToArchive, setProductToArchive] = useState(null);
  const modal = useModal();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const res = await api.get('/products');
      setProducts(res.data);
    } catch (error) {
      modal.error('Error cargando productos');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setForm(initialForm);
    setShowModal(true);
  };

  const openEdit = (product) => {
    setForm({
      id: product.id,
      code: product.code ?? '',
      name: product.name,
      description: product.description ?? '',
      unitPrice: String(product.unitPrice),
      taxRate: String(product.taxRate),
      unit: product.unit ?? 'unidad',
      isActive: Boolean(product.isActive),
      ownerId: product.ownerId ?? null
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setForm(initialForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      const payload = {
        code: form.code,
        name: form.name,
        description: form.description,
        unitPrice: Number(form.unitPrice),
        taxRate: Number(form.taxRate),
        unit: form.unit,
        isActive: form.isActive
      };

      if (form.id) {
        const res = await api.put(`/products/${form.id}`, payload);
        setProducts(products.map((item) => (item.id === form.id ? res.data : item)));
      } else {
        const res = await api.post('/products', payload);
        setProducts([res.data, ...products]);
      }

      closeModal();
    } catch (error) {
      if (error.response?.data?.message) {
        modal.error(error.response.data.message);
      } else {
        modal.error('Error guardando producto');
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await api.delete(`/products/${id}`);
      const updated = await api.get('/products');
      setProducts(updated.data);
      setProductToArchive(null);
      const relatedInvoiceLines = Number(res.data?.relatedInvoiceLines || 0);
      if (relatedInvoiceLines > 0) {
        modal.info(`Producto archivado. Tiene ${relatedInvoiceLines} línea(s) de factura asociada(s).`);
      }
    } catch (error) {
      if (error.response?.data?.message) {
        modal.error(error.response.data.message);
      } else {
        modal.error('Error archivando producto');
      }
    }
  };

  const filteredProducts = products.filter((product) =>
    [product.name, product.code, product.description]
      .filter(Boolean)
      .some((value) => value.toLowerCase().includes(search.toLowerCase()))
  );

  const columns = [
    { key: 'name', header: 'Nombre', className: 'dt-strong', fixed: true, render: (p) => p.name },
    { key: 'code', header: 'Código', render: (p) => p.code || '—' },
    { key: 'unitPrice', header: 'Precio', className: 'num', render: (p) => `${Number(p.unitPrice).toFixed(2)} €` },
    { key: 'taxRate', header: 'IVA', className: 'num', render: (p) => `${Number(p.taxRate).toFixed(0)}%` },
    { key: 'unit', header: 'Unidad', render: (p) => p.unit },
    { key: 'isActive', header: 'Estado', render: (p) => (
      <span className={`badge ${p.isActive ? 'badge-success' : 'badge-gray'}`}>{p.isActive ? 'Activo' : 'Inactivo'}</span>
    ) },
    { key: 'actions', header: '', className: 'col-actions', render: (p) => (
      <div className="dt-actions">
        {hasPermission('products', 'edit') && (
          <button className="btn-icon" onClick={() => openEdit(p)} title="Editar"><Edit2 size={16} /></button>
        )}
        {hasPermission('products', 'delete') && (
          <button className="btn-icon" onClick={() => setProductToArchive(p)} title="Archivar" style={{ color: '#ef4444' }}><Trash2 size={16} /></button>
        )}
      </div>
    ) },
  ];

  const fields = fl.fields(columns);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Productos y servicios</h1>
          <p className="page-subtitle">{products.length} registros</p>
        </div>
        {hasPermission('products', 'create') && (
          <button className="btn btn-primary" onClick={openCreate}>
            <Plus size={18} /> Nuevo producto
          </button>
        )}
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <Search />
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre, código o descripción..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <VistaControles
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              fields={fields}
              hidden={fl.hidden}
              onToggleField={fl.toggle}
              onReorderFields={fl.reorder}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Cargando...</div>
        ) : filteredProducts.length === 0 ? (
          <div className="empty-state">
            <Package size={64} />
            <h3>No hay productos</h3>
            <p>Crea el primer producto para usarlo en facturas</p>
          </div>
        ) : viewMode === 'table' ? (
          <DataTable columns={fl.orderColumns(columns)} rows={filteredProducts} hiddenKeys={fl.hidden} />
        ) : (
          <div className="items-grid">
            {filteredProducts.map((product) => (
              <div className="item-card" key={product.id}>
                <div className="item-card-header">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="item-card-title">{product.name}</div>
                    {!fl.isHidden('code') && product.code && <div className="item-card-subtitle">Cód: {product.code}</div>}
                  </div>
                  {!fl.isHidden('isActive') && (
                    <span className={`badge ${product.isActive ? 'badge-success' : 'badge-gray'}`}>
                      {product.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  )}
                </div>
                {product.description && (
                  <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', lineHeight: 1.4 }}>{product.description}</div>
                )}
                <div className="item-card-body">
                  {!fl.isHidden('unitPrice') && (
                    <div className="item-card-row" style={fl.cardStyle('unitPrice')}>
                      <span className="item-label">Precio</span>
                      <span style={{ fontWeight: 600 }}>{Number(product.unitPrice).toFixed(2)} €</span>
                    </div>
                  )}
                  {!fl.isHidden('taxRate') && (
                    <div className="item-card-row" style={fl.cardStyle('taxRate')}>
                      <span className="item-label">IVA</span>
                      <span>{Number(product.taxRate).toFixed(0)}%</span>
                    </div>
                  )}
                  {!fl.isHidden('unit') && (
                    <div className="item-card-row" style={fl.cardStyle('unit')}>
                      <span className="item-label">Unidad</span>
                      <span>{product.unit}</span>
                    </div>
                  )}
                </div>
                <div className="item-card-footer">
                  <div className="item-card-actions">
                    {hasPermission('products', 'edit') && (
                      <button className="btn btn-outline btn-sm" onClick={() => openEdit(product)}>Editar</button>
                    )}
                    {hasPermission('products', 'delete') && (
                      <button className="btn-icon" onClick={() => setProductToArchive(product)} style={{ color: '#ef4444' }}>
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {productToArchive !== null && (
        <div className="modal-overlay" onClick={() => setProductToArchive(null)}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar archivado</h2>
              <button className="btn-icon" onClick={() => setProductToArchive(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p>El producto <strong>{productToArchive.name}</strong> no se borrará físicamente. Se marcará como inactivo para conservar el historial de facturas.</p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-outline" onClick={() => setProductToArchive(null)}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={() => handleDelete(productToArchive.id)}>Archivar</button>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(event) => event.stopPropagation()}>
            <div className="modal-header">
              <h2>{form.id ? 'Editar producto' : 'Nuevo producto'}</h2>
              <button className="btn-icon" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Codigo</label>
                    <input className="form-input" value={form.code} onChange={(event) => setForm({ ...form, code: event.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nombre *</label>
                    <input className="form-input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Precio unitario *</label>
                    <DecimalInput
                      min="0"
                      step="0.01"
                      value={form.unitPrice}
                      onValueChange={(value) => setForm({ ...form, unitPrice: value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">IVA (%)</label>
                    <DecimalInput
                      min="0"
                      step="0.01"
                      value={form.taxRate}
                      onValueChange={(value) => setForm({ ...form, taxRate: value })}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Unidad</label>
                    <input className="form-input" value={form.unit} onChange={(event) => setForm({ ...form, unit: event.target.value })} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Estado</label>
                    <select
                      className="form-input"
                      value={String(form.isActive)}
                      onChange={(event) => setForm({ ...form, isActive: event.target.value === 'true' })}
                    >
                      <option value="true">Activo</option>
                      <option value="false">Inactivo</option>
                    </select>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Descripcion</label>
                  <textarea
                    className="form-input"
                    rows={3}
                    value={form.description}
                    onChange={(event) => setForm({ ...form, description: event.target.value })}
                  />
                </div>
                {form.id && (
                  <OwnerSelect
                    value={form.ownerId}
                    onChange={(id) => setForm({ ...form, ownerId: id })}
                  />
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={closeModal}>Cancelar</button>
                <button type="submit" className="btn btn-primary">{form.id ? 'Guardar' : 'Crear'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
